// Cave84 — Wishlist service (Phase 5 Week 15)
// Spec §4.9 F9. WishlistEntry collection: top-level (not subcollection).
// Owner can add/edit/remove wishlist entries; viewers can read.

import {
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import type { WishlistEntry, WishlistPriority, WishlistStatus } from "@cave84/shared-types";
import { db } from "./firebase";
import { typedCollection, typedDoc } from "./firestore";

// ---------------------------------------------------------------------------
// Typed collection reference
// ---------------------------------------------------------------------------

const wishlistCol = () => typedCollection<WishlistEntry>("wishlist");

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** All wishlist entries, ordered by priority then date_added desc. */
export async function listWishlistEntries(): Promise<WishlistEntry[]> {
  const snap = await getDocs(query(wishlistCol(), orderBy("date_added", "desc")));
  return snap.docs.map(d => d.data());
}

/** Active wishlist entries only (status === "active").
 * No orderBy — WishlistPage sorts client-side. Avoids composite index requirement. */
export async function listActiveWishlistEntries(): Promise<WishlistEntry[]> {
  const snap = await getDocs(
    query(wishlistCol(), where("status", "==", "active"))
  );
  return snap.docs.map(d => d.data());
}

/** Single entry by ID. Returns null if not found. */
export async function getWishlistEntry(id: string): Promise<WishlistEntry | null> {
  const snap = await getDocs(query(wishlistCol(), where("__name__", "==", id)));
  const doc = snap.docs[0];
  return doc ? doc.data() : null;
}

/**
 * Find active wishlist entries for a specific wine (+ optional vintage year).
 * Used by AddBottlePage to detect matches (F9.6).
 */
export async function findWishlistMatchForWine(
  wineId: string,
  vintageYear?: number
): Promise<WishlistEntry[]> {
  const q = query(
    wishlistCol(),
    where("wine_id", "==", wineId),
    where("status", "==", "active")
  );
  const snap = await getDocs(q);
  const entries = snap.docs.map(d => d.data());
  if (!vintageYear) return entries;
  // Filter: entry has no vintage_year (any vintage) OR matches exactly
  return entries.filter(e => !e.vintage_year || e.vintage_year === vintageYear);
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface WishlistCreateInput {
  wine_id?: string;          // required when wine exists in catalog
  wine_name_free?: string;   // required when wine is not yet in catalog
  vintage_year?: number;
  target_price?: number;
  target_currency?: string;
  priority: WishlistPriority;
  notes?: string;
}

/** Create a new wishlist entry. Returns the created entry. */
export async function createWishlistEntry(input: WishlistCreateInput): Promise<WishlistEntry> {
  if (!input.wine_id && !input.wine_name_free) {
    throw new Error("Either wine_id or wine_name_free is required.");
  }
  const col = wishlistCol();
  const ref = await addDoc(col, {
    ...input,
    status: "active" as WishlistStatus,
    date_added: serverTimestamp(),
  } as Omit<WishlistEntry, "id">);
  return {
    id: ref.id,
    ...input,
    status: "active",
    date_added: serverTimestamp() as WishlistEntry["date_added"],
  } as WishlistEntry;
}

/**
 * Create many entries at once (Inventory bulk action).
 *
 * Entries that already exist as active wishlist rows for the same wine +
 * vintage are skipped — selecting 12 rows and hitting "Add to Wishlist" twice
 * must not produce 24 duplicates. Returns the number actually created.
 */
export async function createWishlistEntries(inputs: WishlistCreateInput[]): Promise<number> {
  if (inputs.length === 0) return 0;

  const existing = await listActiveWishlistEntries();
  const seen = new Set(existing.map(e => `${e.wine_id ?? ''}__${e.vintage_year ?? ''}`));

  const fresh: WishlistCreateInput[] = [];
  for (const input of inputs) {
    if (!input.wine_id && !input.wine_name_free) continue;
    const key = `${input.wine_id ?? ''}__${input.vintage_year ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    fresh.push(input);
  }
  if (fresh.length === 0) return 0;

  // Firestore caps a batch at 500 writes; wishlist rows are one write each.
  for (let i = 0; i < fresh.length; i += 400) {
    const batch = writeBatch(db);
    for (const input of fresh.slice(i, i + 400)) {
      batch.set(doc(wishlistCol()), {
        ...input,
        status: "active" as WishlistStatus,
        date_added: serverTimestamp(),
      } as Omit<WishlistEntry, "id">);
    }
    await batch.commit();
  }

  return fresh.length;
}

export interface WishlistUpdateInput {
  priority?: WishlistPriority;
  target_price?: number;
  target_currency?: string;
  notes?: string;
  status?: WishlistStatus;
  acquired_bottle_ids?: string[];
}

/** Update mutable fields on a wishlist entry. */
export async function updateWishlistEntry(
  id: string,
  input: WishlistUpdateInput
): Promise<void> {
  const updates: Record<string, unknown> = { ...input };
  if (input.status && input.status !== "active") {
    updates["date_status_changed"] = serverTimestamp();
  }
  await updateDoc(typedDoc<WishlistEntry>("wishlist", id), updates as Partial<WishlistEntry>);
}

/**
 * Mark entry as acquired. Optionally attach bottle IDs.
 * Also sets date_status_changed.
 */
export async function markWishlistAcquired(
  id: string,
  bottleIds: string[] = []
): Promise<void> {
  await updateDoc(typedDoc<WishlistEntry>("wishlist", id), {
    status: "acquired",
    date_status_changed: serverTimestamp(),
    acquired_bottle_ids: bottleIds,
  } as Partial<WishlistEntry>);
}

/** Hard-delete a wishlist entry (allowed — wishlist entries are not inventory). */
export async function deleteWishlistEntry(id: string): Promise<void> {
  await deleteDoc(typedDoc<WishlistEntry>("wishlist", id));
}
